import { useEffect, useState } from 'react';
import { Layout } from '../components/layout/Layout';
import { DataTable } from '../components/common/DataTable';
import { StatusBadge } from '../components/common/StatusBadge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Plus, Search, MoreHorizontal, Pencil, Trash2, Truck, Gauge } from 'lucide-react';
import { getVehicles, getDrivers, createVehicle, updateVehicle, deleteVehicle } from '../lib/api';
import { formatDate } from '../lib/utils';
import { toast } from 'sonner';

const VEHICLE_TYPES = [
    { value: 'truk_tangki', label: 'Truk Tangki' },
    { value: 'trailer', label: 'Trailer' },
    { value: 'pickup', label: 'Pickup' },
    { value: 'mobil_service', label: 'Mobil Service' }
];

const VEHICLE_STATUSES = [
    { value: 'operasional', label: 'Operasional' },
    { value: 'maintenance', label: 'Maintenance' },
    { value: 'tidak_aktif', label: 'Tidak Aktif' },
    { value: 'rusak', label: 'Rusak' }
];

const POOLS = ['Pool Jakarta', 'Pool Bekasi', 'Pool Tangerang', 'Pool Bogor', 'Pool Depok'];

export default function VehiclesPage() {
    const [vehicles, setVehicles] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [odometerDialogOpen, setOdometerDialogOpen] = useState(false);
    const [editingVehicle, setEditingVehicle] = useState(null);
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [newOdometer, setNewOdometer] = useState('');
    const [formData, setFormData] = useState({
        nomor_polisi: '',
        jenis_kendaraan: 'truk_tangki',
        merk: '',
        model: '',
        tahun_pembuatan: new Date().getFullYear(),
        nomor_rangka: '',
        nomor_mesin: '',
        kapasitas_tangki: 16000,
        odometer: 0,
        pool_area: 'Pool Jakarta',
        status: 'operasional',
        driver_id: '',
        tanggal_kir: '',
        tanggal_stnk: '',
        tanggal_asuransi: '',
        catatan: ''
    });

    useEffect(() => {
        fetchData();
    }, [statusFilter, typeFilter]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const params = {};
            if (statusFilter !== 'all') params.status = statusFilter;
            if (typeFilter !== 'all') params.jenis = typeFilter;
            
            const [vehiclesRes, driversRes] = await Promise.all([
                getVehicles(params),
                getDrivers({ status: 'aktif' })
            ]);
            setVehicles(vehiclesRes.data);
            setDrivers(driversRes.data);
        } catch (error) {
            console.error('Failed to fetch data:', error);
            toast.error('Gagal memuat data');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (vehicle = null) => {
        if (vehicle) {
            setEditingVehicle(vehicle);
            setFormData({
                nomor_polisi: vehicle.nomor_polisi || '',
                jenis_kendaraan: vehicle.jenis_kendaraan || 'truk_tangki',
                merk: vehicle.merk || '',
                model: vehicle.model || '',
                tahun_pembuatan: vehicle.tahun_pembuatan || new Date().getFullYear(),
                nomor_rangka: vehicle.nomor_rangka || '',
                nomor_mesin: vehicle.nomor_mesin || '',
                kapasitas_tangki: vehicle.kapasitas_tangki || 16000,
                odometer: vehicle.odometer || 0,
                pool_area: vehicle.pool_area || 'Pool Jakarta',
                status: vehicle.status || 'operasional',
                driver_id: vehicle.driver_id || '',
                tanggal_kir: vehicle.tanggal_kir ? vehicle.tanggal_kir.split('T')[0] : '',
                tanggal_stnk: vehicle.tanggal_stnk ? vehicle.tanggal_stnk.split('T')[0] : '',
                tanggal_asuransi: vehicle.tanggal_asuransi ? vehicle.tanggal_asuransi.split('T')[0] : '',
                catatan: vehicle.catatan || ''
            });
        } else {
            setEditingVehicle(null);
            setFormData({
                nomor_polisi: '',
                jenis_kendaraan: 'truk_tangki',
                merk: '',
                model: '',
                tahun_pembuatan: new Date().getFullYear(),
                nomor_rangka: '',
                nomor_mesin: '',
                kapasitas_tangki: 16000,
                odometer: 0,
                pool_area: 'Pool Jakarta',
                status: 'operasional',
                driver_id: '',
                tanggal_kir: '',
                tanggal_stnk: '',
                tanggal_asuransi: '',
                catatan: ''
            });
        }
        setDialogOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const submitData = {
                ...formData,
                tanggal_kir: formData.tanggal_kir ? new Date(formData.tanggal_kir).toISOString() : null,
                tanggal_stnk: formData.tanggal_stnk ? new Date(formData.tanggal_stnk).toISOString() : null,
                tanggal_asuransi: formData.tanggal_asuransi ? new Date(formData.tanggal_asuransi).toISOString() : null,
                driver_id: formData.driver_id || null
            };

            if (editingVehicle) {
                await updateVehicle(editingVehicle.id, submitData);
                toast.success('Kendaraan berhasil diupdate');
            } else {
                await createVehicle(submitData);
                toast.success('Kendaraan berhasil ditambahkan');
            }
            setDialogOpen(false);
            fetchData();
        } catch (error) {
            console.error('Failed to save vehicle:', error);
            toast.error('Gagal menyimpan kendaraan');
        }
    };

    const handleDelete = async (vehicle) => {
        if (window.confirm(`Yakin ingin menghapus "${vehicle.nomor_polisi}"?`)) {
            try {
                await deleteVehicle(vehicle.id);
                toast.success('Kendaraan berhasil dihapus');
                fetchData();
            } catch (error) {
                console.error('Failed to delete vehicle:', error);
                toast.error('Gagal menghapus kendaraan');
            }
        }
    };

    const filteredVehicles = vehicles.filter(v =>
        v.nomor_polisi.toLowerCase().includes(searchQuery.toLowerCase()) ||
        v.merk.toLowerCase().includes(searchQuery.toLowerCase()) ||
        v.pool_area.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const columns = [
        {
            header: 'No. Polisi',
            accessor: 'nomor_polisi',
            render: (value) => (
                <span className="font-mono text-xs uppercase tracking-wider text-primary font-medium">
                    {value}
                </span>
            )
        },
        {
            header: 'Kendaraan',
            accessor: 'merk',
            render: (value, row) => (
                <div>
                    <p className="font-medium">{value} {row.model}</p>
                    <p className="text-xs text-muted-foreground capitalize">{row.jenis_kendaraan?.replace('_', ' ')}</p>
                </div>
            )
        },
        {
            header: 'Kapasitas',
            accessor: 'kapasitas_tangki',
            render: (value) => <span className="text-sm">{value?.toLocaleString()} L</span>
        },
        {
            header: 'Odometer',
            accessor: 'odometer',
            render: (value) => <span className="text-sm font-mono">{value?.toLocaleString()} km</span>
        },
        {
            header: 'Pool',
            accessor: 'pool_area'
        },
        {
            header: 'Sopir',
            accessor: 'driver_name',
            render: (value) => <span className="text-sm">{value || '-'}</span>
        },
        {
            header: 'Status',
            accessor: 'status',
            render: (value) => <StatusBadge status={value} />
        },
        {
            header: '',
            accessor: 'actions',
            render: (_, row) => (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`vehicle-actions-${row.id}`}>
                            <MoreHorizontal className="h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => {
                            setSelectedVehicle(row);
                            setNewOdometer(row.odometer?.toString() || '0');
                            setOdometerDialogOpen(true);
                        }}>
                            <Gauge className="h-4 w-4 mr-2" />
                            Update Odometer
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleOpenDialog(row)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                            onClick={() => handleDelete(row)}
                            className="text-destructive focus:text-destructive"
                        >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Hapus
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            )
        }
    ];

    return (
        <Layout title="Armada">
            <div className="space-y-6" data-testid="vehicles-page">
                {/* Header Actions */}
                <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
                    <div className="flex flex-1 gap-3 w-full sm:w-auto flex-wrap">
                        <div className="relative flex-1 sm:w-64 min-w-[200px]">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                placeholder="Cari kendaraan..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-9 rounded-sm"
                                data-testid="search-vehicles"
                            />
                        </div>
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger className="w-[140px] rounded-sm" data-testid="status-filter">
                                <SelectValue placeholder="Status" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Semua Status</SelectItem>
                                {VEHICLE_STATUSES.map(s => (
                                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={typeFilter} onValueChange={setTypeFilter}>
                            <SelectTrigger className="w-[140px] rounded-sm" data-testid="type-filter">
                                <SelectValue placeholder="Jenis" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Semua Jenis</SelectItem>
                                {VEHICLE_TYPES.map(t => (
                                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <Button 
                        onClick={() => handleOpenDialog()} 
                        className="industrial-btn-primary"
                        data-testid="add-vehicle-btn"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Tambah Armada
                    </Button>
                </div>

                {/* Vehicles Table */}
                <DataTable
                    columns={columns}
                    data={filteredVehicles}
                    loading={loading}
                    emptyMessage="Tidak ada kendaraan"
                    emptyIcon={Truck}
                />

                {/* Add/Edit Dialog */}
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="section-title">
                                {editingVehicle ? 'Edit Kendaraan' : 'Tambah Kendaraan Baru'}
                            </DialogTitle>
                            <DialogDescription>
                                {editingVehicle ? 'Update informasi kendaraan.' : 'Masukkan data kendaraan baru.'}
                            </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleSubmit}>
                            <div className="grid grid-cols-3 gap-4 py-4">
                                <div className="space-y-2">
                                    <Label htmlFor="nomor_polisi">Nomor Polisi *</Label>
                                    <Input
                                        id="nomor_polisi"
                                        value={formData.nomor_polisi}
                                        onChange={(e) => setFormData({ ...formData, nomor_polisi: e.target.value.toUpperCase() })}
                                        required
                                        className="rounded-sm font-mono"
                                        placeholder="B 1234 ABC"
                                        data-testid="vehicle-nopol-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="jenis_kendaraan">Jenis Kendaraan *</Label>
                                    <Select 
                                        value={formData.jenis_kendaraan} 
                                        onValueChange={(value) => setFormData({ ...formData, jenis_kendaraan: value })}
                                    >
                                        <SelectTrigger className="rounded-sm" data-testid="vehicle-type-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {VEHICLE_TYPES.map(t => (
                                                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="status">Status</Label>
                                    <Select 
                                        value={formData.status} 
                                        onValueChange={(value) => setFormData({ ...formData, status: value })}
                                    >
                                        <SelectTrigger className="rounded-sm" data-testid="vehicle-status-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {VEHICLE_STATUSES.map(s => (
                                                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="merk">Merk *</Label>
                                    <Input
                                        id="merk"
                                        value={formData.merk}
                                        onChange={(e) => setFormData({ ...formData, merk: e.target.value })}
                                        required
                                        className="rounded-sm"
                                        placeholder="Hino, Isuzu, dll"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="model">Model *</Label>
                                    <Input
                                        id="model"
                                        value={formData.model}
                                        onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                                        required
                                        className="rounded-sm"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="tahun_pembuatan">Tahun</Label>
                                    <Input
                                        id="tahun_pembuatan"
                                        type="number"
                                        value={formData.tahun_pembuatan}
                                        onChange={(e) => setFormData({ ...formData, tahun_pembuatan: parseInt(e.target.value) })}
                                        className="rounded-sm"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="nomor_rangka">Nomor Rangka *</Label>
                                    <Input
                                        id="nomor_rangka"
                                        value={formData.nomor_rangka}
                                        onChange={(e) => setFormData({ ...formData, nomor_rangka: e.target.value.toUpperCase() })}
                                        required
                                        className="rounded-sm font-mono"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="nomor_mesin">Nomor Mesin</Label>
                                    <Input
                                        id="nomor_mesin"
                                        value={formData.nomor_mesin}
                                        onChange={(e) => setFormData({ ...formData, nomor_mesin: e.target.value.toUpperCase() })}
                                        className="rounded-sm font-mono"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="kapasitas_tangki">Kapasitas Tangki (L) *</Label>
                                    <Input
                                        id="kapasitas_tangki"
                                        type="number"
                                        value={formData.kapasitas_tangki}
                                        onChange={(e) => setFormData({ ...formData, kapasitas_tangki: parseFloat(e.target.value) })}
                                        required
                                        className="rounded-sm"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="odometer">Odometer (km)</Label>
                                    <Input
                                        id="odometer"
                                        type="number"
                                        value={formData.odometer}
                                        onChange={(e) => setFormData({ ...formData, odometer: parseFloat(e.target.value) })}
                                        className="rounded-sm"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="pool_area">Pool Area *</Label>
                                    <Select 
                                        value={formData.pool_area} 
                                        onValueChange={(value) => setFormData({ ...formData, pool_area: value })}
                                    >
                                        <SelectTrigger className="rounded-sm">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {POOLS.map(p => (
                                                <SelectItem key={p} value={p}>{p}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="driver_id">Sopir</Label>
                                    <Select 
                                        value={formData.driver_id || "none"} 
                                        onValueChange={(value) => setFormData({ ...formData, driver_id: value === "none" ? "" : value })}
                                    >
                                        <SelectTrigger className="rounded-sm">
                                            <SelectValue placeholder="Pilih sopir" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">Tidak ada</SelectItem>
                                            {drivers.filter(d => !d.vehicle_id || d.id === editingVehicle?.driver_id).map(d => (
                                                <SelectItem key={d.id} value={d.id}>{d.nama}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                
                                <div className="col-span-3 border-t pt-4 mt-2">
                                    <p className="text-sm font-medium mb-3">Dokumen</p>
                                    <div className="grid grid-cols-3 gap-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="tanggal_kir">Expired KIR</Label>
                                            <Input
                                                id="tanggal_kir"
                                                type="date"
                                                value={formData.tanggal_kir}
                                                onChange={(e) => setFormData({ ...formData, tanggal_kir: e.target.value })}
                                                className="rounded-sm"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="tanggal_stnk">Expired STNK</Label>
                                            <Input
                                                id="tanggal_stnk"
                                                type="date"
                                                value={formData.tanggal_stnk}
                                                onChange={(e) => setFormData({ ...formData, tanggal_stnk: e.target.value })}
                                                className="rounded-sm"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="tanggal_asuransi">Expired Asuransi</Label>
                                            <Input
                                                id="tanggal_asuransi"
                                                type="date"
                                                value={formData.tanggal_asuransi}
                                                onChange={(e) => setFormData({ ...formData, tanggal_asuransi: e.target.value })}
                                                className="rounded-sm"
                                            />
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="col-span-3 space-y-2">
                                    <Label htmlFor="catatan">Catatan</Label>
                                    <Textarea
                                        id="catatan"
                                        value={formData.catatan}
                                        onChange={(e) => setFormData({ ...formData, catatan: e.target.value })}
                                        className="rounded-sm"
                                        rows={2}
                                    />
                                </div>
                            </div>
                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">
                                    Batal
                                </Button>
                                <Button type="submit" className="industrial-btn-primary" data-testid="save-vehicle-btn">
                                    {editingVehicle ? 'Update' : 'Simpan'}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>

                {/* Odometer Update Dialog */}
                <Dialog open={odometerDialogOpen} onOpenChange={setOdometerDialogOpen}>
                    <DialogContent className="max-w-md">
                        <DialogHeader>
                            <DialogTitle className="section-title">Update Odometer</DialogTitle>
                            <DialogDescription>
                                Update odometer untuk {selectedVehicle?.nomor_polisi}
                            </DialogDescription>
                        </DialogHeader>
                        <div className="py-4 space-y-4">
                            <div className="space-y-2">
                                <Label>Odometer Saat Ini</Label>
                                <p className="text-2xl font-bold font-mono">{selectedVehicle?.odometer?.toLocaleString()} km</p>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="new_odometer">Odometer Baru (km)</Label>
                                <Input
                                    id="new_odometer"
                                    type="number"
                                    value={newOdometer}
                                    onChange={(e) => setNewOdometer(e.target.value)}
                                    className="rounded-sm font-mono"
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setOdometerDialogOpen(false)} className="rounded-sm">
                                Batal
                            </Button>
                            <Button 
                                onClick={async () => {
                                    try {
                                        await updateVehicle(selectedVehicle.id, { odometer: parseFloat(newOdometer) });
                                        toast.success('Odometer berhasil diupdate');
                                        setOdometerDialogOpen(false);
                                        fetchData();
                                    } catch (error) {
                                        toast.error('Gagal update odometer');
                                    }
                                }}
                                className="industrial-btn-primary"
                            >
                                Update
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
}
